"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import { ArrowRight, Mail, Home, Search, Sparkles } from "lucide-react";

/**
 * THEME INTEGRATION:
 * This component uses CSS variables:
 * var(--primary) -> Elegant Pink/Purple
 * var(--secondary) -> Vibrant Green
 */

interface ButtonData {
  text: string;
  url: string;
  variant?: "primary" | "secondary" | "outline";
  icon?: React.ReactNode;
}

interface SectionData {
  button?: ButtonData[];
  [key: string]: any;
}

interface CustomErrorPageProps {
  data?: {
    subtitle?: string;
    title?: string;
    description?: string;
    sections?: SectionData[];
    primaryCta?: ButtonData;
    secondaryCta?: ButtonData;
  };
}

export default function CustomErrorPage({ data }: CustomErrorPageProps) {
  const heading = data?.subtitle || "Lost in Elegance";
  const title = data?.title || "404";
  const description =
    data?.description ||
    "The piece you are looking for isn't in our collection right now. It might have been moved or is no longer available.";

  const buttons: ButtonData[] =
    data?.sections
      ?.filter((section): section is SectionData => Array.isArray(section?.button))
      ?.flatMap((section) => section.button)
      ?.filter((btn: any): btn is ButtonData => btn?.text && btn?.url) || [];

  const primaryButton: ButtonData = buttons[0] ||
    data?.primaryCta || {
      text: "Back to Home",
      url: "/",
      variant: "primary",
      icon: <Home className="w-4 h-4" />,
    };

  const secondaryButton: ButtonData = buttons[1] ||
    data?.secondaryCta || {
      text: "Get in Touch",
      url: "/contact",
      variant: "outline",
      icon: <Mail className="w-4 h-4" />,
    };

  const getButtonClasses = (button: ButtonData) => {
    const base = "relative overflow-hidden group rounded-full px-10 py-4 text-sm font-bold tracking-widest uppercase transition-all duration-500 flex items-center justify-center gap-3 shadow-md hover:shadow-2xl transform hover:-translate-y-1";

    switch (button.variant) {
      case "primary":
        return `${base} text-white`; // Background set via inline style for var access
      case "secondary":
        return `${base} text-white`; 
      case "outline":
        return `${base} bg-transparent text-neutral-900 border-2 hover:text-white`;
      default:
        return `${base} bg-neutral-900 text-white`;
    }
  };

  const getButtonStyle = (variant?: string) => {
    if (variant === "primary") return { backgroundColor: "var(--secondary)" };
    if (variant === "secondary") return { backgroundColor: "var(--primary)" };
    if (variant === "outline") return { borderColor: "var(--secondary)" };
    return {};
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col items-center justify-center px-6 py-20 relative overflow-hidden">
      
      {/* --- AESTHETIC BACKGROUND --- */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Diamond Cut Grid Overlay using CSS Variable for Fill */}
        <div className="absolute inset-0 opacity-[0.03]" 
             style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0l30 30-30 30L0 30z' fill='gray' fill-rule='evenodd'/%3E%3C/svg%3E")` }} 
        />
        
        {/* Ambient Glows using CSS Variables */}
        <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] rounded-full opacity-10 blur-[120px] animate-pulse" 
             style={{ backgroundColor: "var(--secondary)" }} />
        <div className="absolute bottom-[-10%] left-[-5%] w-[600px] h-[600px] rounded-full opacity-10 blur-[120px] animate-pulse" 
             style={{ backgroundColor: "var(--primary)", animationDelay: '2s' }} />
      </div>

      <div className="w-full max-w-7xl grid lg:grid-cols-2 gap-16 items-center relative z-10">
        
        {/* --- LEFT: CONTENT --- */}
        <div className="order-2 lg:order-1 flex flex-col items-center lg:items-start text-center lg:text-left">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-3 mb-8"
          >
            <div className="h-[1px] w-8" style={{ backgroundColor: "var(--primary)" }} />
            <Sparkles className="w-5 h-5" style={{ color: "var(--secondary)" }} />
            <span className="text-xs font-black tracking-[0.4em] uppercase" style={{ color: "var(--primary)" }}>
              {heading}
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, type: "spring" }}
            className="text-9xl md:text-[12rem] font-light text-neutral-900 leading-none mb-6 relative"
          >
            {title}
            <span className="absolute -top-4 -right-8 text-2xl font-bold opacity-30" style={{ color: "var(--secondary)" }}>#404</span>
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-8 max-w-lg"
          >
            <p className="text-gray-700 text-xl md:text-2xl font-light leading-relaxed italic">
              &quot;{description}&quot;
            </p>
            
            <div className="flex items-center gap-4 group cursor-pointer">
              <div className="h-px flex-1 bg-neutral-200 group-hover:opacity-100 transition-opacity" style={{ backgroundColor: "var(--secondary)" }} />
              <Link href="/collections" className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-900 hover:opacity-70 transition-opacity" style={{ color: "var(--primary)" }}>
                Explore Collections
              </Link>
              <div className="h-px flex-1 bg-neutral-200 group-hover:opacity-100 transition-opacity" style={{ backgroundColor: "var(--secondary)" }} />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-6 mt-12 w-full sm:w-auto"
          >
            <Link href={primaryButton.url} className="w-full sm:w-auto">
              <button 
                className={getButtonClasses(primaryButton)}
                style={getButtonStyle(primaryButton.variant)}
              >
                <Home className="w-4 h-4" />
                <span>{primaryButton.text}</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </Link>
            
            <Link href={secondaryButton.url} className="w-full sm:w-auto">
              <button 
                className={getButtonClasses(secondaryButton)}
                style={getButtonStyle(secondaryButton.variant)}
                onMouseEnter={(e) => {
                  if(secondaryButton.variant === 'outline') {
                    e.currentTarget.style.backgroundColor = "var(--secondary)";
                  }
                }}
                onMouseLeave={(e) => {
                  if(secondaryButton.variant === 'outline') {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }
                }}
              >
                <Mail className="w-4 h-4" />
                <span>{secondaryButton.text}</span>
              </button>
            </Link>
          </motion.div>
        </div>

        {/* --- RIGHT: VISUAL ILLUSTRATION --- */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="order-1 lg:order-2 relative flex justify-center"
        >
          <div className="relative w-72 h-72 md:w-[500px] md:h-[500px] flex items-center justify-center">
            
            {/* Spinning Jewelry Rings */}
            <motion.div 
              animate={{ rotate: 360 }}
              transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
              className="absolute inset-0 border-[1px] rounded-full" 
              style={{ borderColor: "var(--secondary)", opacity: 0.4 }}
            />
            <motion.div 
              animate={{ rotate: -360 }}
              transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
              className="absolute inset-12 border-[1px] rounded-full" 
              style={{ borderColor: "var(--primary)", opacity: 0.3 }}
            />
            <motion.div 
              animate={{ rotate: 360 }}
              transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
              className="absolute inset-24 border-[2px] border-transparent rounded-full" 
              style={{ borderTopColor: "var(--primary)", opacity: 0.2 }}
            />
            
            {/* Center Floating Icon Card */}
            <div className="relative">
              <motion.div
                animate={{ y: [0, -20, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                className="bg-white/80 backdrop-blur-md p-10 md:p-16 rounded-[3rem] shadow-xl relative z-10 border border-white"
              >
                <Search className="w-20 h-20 md:w-32 md:h-32 stroke-[1px]" style={{ color: "var(--secondary)" }} />
              </motion.div>

              {/* Decorative Floating Orbs */}
              <motion.div 
                animate={{ x: [0, 20, 0], y: [0, 20, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
                className="absolute -top-6 -right-6 w-14 h-14 rounded-full blur-xl opacity-50" 
                style={{ backgroundColor: "var(--primary)" }}
              />
              <motion.div 
                animate={{ x: [0, -20, 0], y: [0, -20, 0] }}
                transition={{ duration: 6, repeat: Infinity, delay: 1 }}
                className="absolute -bottom-8 -left-8 w-20 h-20 rounded-full blur-xl opacity-40" 
                style={{ backgroundColor: "var(--secondary)" }}
              />
            </div>
          </div>
        </motion.div>
      </div>

      {/* --- FOOTER DECOR --- */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="absolute bottom-10 flex flex-col items-center gap-4"
      >
        <div className="h-10 w-[1px] bg-gradient-to-b from-transparent" style={{ backgroundImage: "linear-gradient(to bottom, transparent, var(--secondary))" }} />
        <span className="text-[10px] uppercase tracking-[0.6em] text-gray-700 font-bold">
          Pure Elegance • Crafted Perfection
        </span>
      </motion.div>
    </div>
  );
}